import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useApp } from '@/components/AppLayout';
import { LogOut, CreditCard, RefreshCw, ChevronRight, Building2, Palette, FileText, Users, User, Sparkles, Percent, Star, Mail, Receipt, Bell, BellRing, ShieldAlert, Loader2, Banknote } from 'lucide-react';
import { clearTokens, saveTokens, authApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  ListPage,
  ListPageHeader,
  ListPageTitle,
} from '@/components/ui/list-page';

interface SettingsItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  iconBg?: string;
}

function SettingsItem({ icon, title, description, onClick, iconBg = 'bg-muted' }: SettingsItemProps) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-muted/50 transition-colors text-left group border-b"
    >
      <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center shrink-0', iconBg)}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
    </button>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

function Section({ title, children, className }: SectionProps) {
  return (
    <div className={cn('bg-card rounded-xl border shadow-sm overflow-hidden break-inside-avoid mb-4', className)}>
      <div className="px-5 py-3 border-b bg-muted/30">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </h2>
      </div>
      <div>
        {children}
      </div>
    </div>
  );
}

const ADMIN_EMAILS = [
  'zain.allarakhia@gmail.com',
  'josh.mangel@gmail.com',
  'zain@lefage.com',
  'josh@lefage.com',
];

function SettingsContent() {
  const navigate = useNavigate();
  const { nav } = useBusinessNav();
  const { isViewOnly } = useApp();

  // Admin impersonation state
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [impersonateEmail, setImpersonateEmail] = useState('');
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState('');

  useEffect(() => {
    authApi.getMe().then(res => {
      if (res.data) {
        setUserEmail(res.data.email);
      }
    });
  }, []);

  const isAdmin = userEmail && ADMIN_EMAILS.includes(userEmail);

  const handleImpersonate = async () => {
    if (!impersonateEmail.trim()) return;

    setImpersonating(true);
    setImpersonateError('');

    const response = await authApi.impersonate(impersonateEmail.trim());

    if (response.error) {
      setImpersonateError(response.error);
      setImpersonating(false);
      return;
    }

    if (response.data && 'tokens' in response.data) {
      clearTokens();
      saveTokens(response.data.tokens);
      window.location.href = '/';
    }
  };

  const handleSignOut = () => {
    clearTokens();
    navigate('/login');
  };

  return (
    <ListPage>
      <ListPageHeader>
        <ListPageTitle
          subtitle="Manage your account and business preferences"
        >
          Settings
        </ListPageTitle>
        <div />
      </ListPageHeader>

      <div className="px-6 columns-1 lg:columns-2 gap-4">
        <Section title="Profile">
          <SettingsItem
            icon={<User className="h-5 w-5 text-blue-600" />}
            iconBg="bg-blue-100 dark:bg-blue-950"
            title="Personal Info"
            description="Your name and account details"
            onClick={() => nav('/settings/profile')}
          />
        </Section>

        <Section title="Integrations">
          {!isViewOnly && (
            <SettingsItem
              icon={<CreditCard className="h-5 w-5 text-green-600" />}
              iconBg="bg-green-100 dark:bg-green-950"
              title="Payment Processing"
              description="Accept payments with Stripe"
              onClick={() => nav('/settings/payments')}
            />
          )}
          <SettingsItem
            icon={<RefreshCw className="h-5 w-5 text-cyan-600" />}
            iconBg="bg-cyan-100 dark:bg-cyan-950"
            title="Accounting Sync"
            description="Connect QuickBooks and more"
            onClick={() => nav('/settings/accounting')}
          />
        </Section>

        {!isViewOnly && (
          <Section title="Business">
            <SettingsItem
              icon={<Building2 className="h-5 w-5 text-violet-600" />}
              iconBg="bg-violet-100 dark:bg-violet-950"
              title="Business Information"
              description="Logo, name, address, industry"
              onClick={() => nav('/settings/business')}
            />
            <SettingsItem
              icon={<Users className="h-5 w-5 text-indigo-600" />}
              iconBg="bg-indigo-100 dark:bg-indigo-950"
              title="Team Members"
              description="Manage access and permissions"
              onClick={() => nav('/settings/team')}
            />
            <SettingsItem
              icon={<Percent className="h-5 w-5 text-emerald-600" />}
              iconBg="bg-emerald-100 dark:bg-emerald-950"
              title="Tax Rates"
              description="Configure taxes for invoices"
              onClick={() => nav('/taxes')}
            />
            <SettingsItem
              icon={<Star className="h-5 w-5 text-amber-600" />}
              iconBg="bg-amber-100 dark:bg-amber-950"
              title="Client Reviews"
              description="Collect feedback from customers"
              onClick={() => nav('/settings/client-reviews')}
            />
          </Section>
        )}

        {!isViewOnly && (
          <Section title="Subscription">
            <SettingsItem
              icon={<Sparkles className="h-5 w-5 text-purple-600" />}
              iconBg="bg-purple-100 dark:bg-purple-950"
              title="Plan & Billing"
              description="Manage your subscription"
              onClick={() => nav('/settings/billing')}
            />
          </Section>
        )}

        {!isViewOnly && (
          <Section title="Notifications">
            <SettingsItem
              icon={<BellRing className="h-5 w-5 text-orange-600" />}
              iconBg="bg-orange-100 dark:bg-orange-950"
              title="Notification Preferences"
              description="Email and push notification settings"
              onClick={() => nav('/settings/notifications')}
            />
          </Section>
        )}

        {!isViewOnly && (
          <Section title="Documents">
            <SettingsItem
              icon={<Palette className="h-5 w-5 text-pink-600" />}
              iconBg="bg-pink-100 dark:bg-pink-950"
              title="Template Designer"
              description="Customize invoice and quote layouts"
              onClick={() => nav('/template-designer')}
            />
            <SettingsItem
              icon={<FileText className="h-5 w-5 text-orange-600" />}
              iconBg="bg-orange-100 dark:bg-orange-950"
              title="Contracts"
              description="Create and manage contract templates"
              onClick={() => nav('/contract-editor')}
            />
            <SettingsItem
              icon={<Mail className="h-5 w-5 text-sky-600" />}
              iconBg="bg-sky-100 dark:bg-sky-950"
              title="Message Templates"
              description="Customize emails and reminders"
              onClick={() => nav('/settings/message-templates')}
            />
            <SettingsItem
              icon={<Receipt className="h-5 w-5 text-teal-600" />}
              iconBg="bg-teal-100 dark:bg-teal-950"
              title="Receipts"
              description="Configure payment receipt settings"
              onClick={() => nav('/settings/receipts')}
            />
            <SettingsItem
              icon={<Banknote className="h-5 w-5 text-lime-600" />}
              iconBg="bg-lime-100 dark:bg-lime-950"
              title="Payment Instructions"
              description="Instructions for how clients should pay"
              onClick={() => nav('/settings/payment-instructions')}
            />
            <SettingsItem
              icon={<Bell className="h-5 w-5 text-rose-600" />}
              iconBg="bg-rose-100 dark:bg-rose-950"
              title="Automated Reminders"
              description="Send invoice and quote reminders automatically"
              onClick={() => nav('/settings/reminders')}
            />
          </Section>
        )}

        {isAdmin && !isViewOnly && (
          <Section title="Admin">
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center gap-2 text-amber-600">
                <ShieldAlert className="h-4 w-4" />
                <span className="text-sm font-medium">Login as User</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter a user's email to log in as them. This will sign you out.
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={impersonateEmail}
                  onChange={(e) => setImpersonateEmail(e.target.value)}
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && handleImpersonate()}
                />
                <Button
                  onClick={handleImpersonate}
                  disabled={impersonating || !impersonateEmail.trim()}
                  variant="outline"
                >
                  {impersonating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Login'
                  )}
                </Button>
              </div>
              {impersonateError && (
                <p className="text-sm text-destructive">{impersonateError}</p>
              )}
            </div>
          </Section>
        )}

        <Section title="Account">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-4 px-5 py-4 hover:bg-destructive/10 transition-colors text-left group border-b text-muted-foreground hover:text-destructive"
          >
            <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 bg-red-100 dark:bg-red-950">
              <LogOut className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Sign out</p>
              <p className="text-xs text-muted-foreground">Sign out of your account</p>
            </div>
          </button>
        </Section>
      </div>
    </ListPage>
  );
}

export function Settings() {
  return <SettingsContent />;
}
